<!--
  HelpFlyover.vue -- help as a slide-in panel over the app.

  Jake's review of the shipped help surface: "this UI should be a flyover
  like the store preview, not a dedicated page in the app. The routes can
  stay the same and be loaded in an iframe in the flyover, which would
  preserve the ability to link to them directly."

  So this component is deliberately NOT a second help renderer. It is a
  drawer with an iframe in it, and the iframe points at the SAME
  `/distributor/help/owner/*` and `/help/shopper/*` routes that already
  exist. Consequences worth being explicit about, because they are the
  reason for the design:

    * A direct link, a bookmark, and the open-in-new-tab control below all
      still render a real standalone page. Nothing was redirected or
      deleted. (`router/__tests__/help-framed.spec.js` guards that.)
    * There is ONE implementation of help rendering (HelpRenderer), so the
      flyover cannot drift from the standalone page.
    * The iframe src carries `?framed=1`, which App.vue reads to render the
      framed copy in PublicLayout instead of the surface layout. Without
      it, the iframe would nest a whole second sidebar + topbar inside the
      panel. See `router/helpFramed.js` for that mechanism in full.

  CHROME MIRRORS THE STOREFRONT PREVIEW DRAWER, on purpose -- Jake asked
  for "a flyover like the store preview", and that pattern is already in
  the product at StoreEditShell.vue's `.ces-preview-*` block: fixed scrim
  with click-to-close, right-anchored panel that transforms in, a head row
  carrying the title and controls, and a body that only mounts its iframe
  while open. The class names here are the flyover's own (`hf-*`) rather
  than reused `ces-*` ones, because those are scoped to the store editor
  and this renders on every distributor and storefront screen.

  CONTEXTUAL, like the nav entry it replaces. The iframe target comes from
  `resolveHelpTarget` -- the same resolver, the same map -- so opening Help
  on the store editor still opens "Build a store". It is a COMPUTED, not a
  captured value: the flyover is mounted once by the layout and outlives
  many navigations.

  data-testid values:
    help-flyover-scrim    - the click-to-close backdrop
    help-flyover-panel    - the panel itself (role=dialog)
    help-flyover-iframe   - the embedded help route
    help-flyover-new-tab  - open in a new tab AND dismiss
    help-flyover-close    - the close control
-->
<template>
  <div class="hf-root">
    <div
      class="hf-scrim"
      :class="{ open }"
      data-testid="help-flyover-scrim"
      @click="close"
    ></div>

    <!--
      `inert` while closed, not just aria-hidden.

      The panel stays mounted (it only slides off screen), so its close and
      open-in-new-tab buttons remain in the DOM and in the tab order. With
      aria-hidden alone that is the classic broken combination: a keyboard
      user tabs into controls that are invisible and inside a subtree
      screen readers have been told to ignore. `inert` removes them from
      the tab order and the accessibility tree together, which is the
      whole point of the attribute.

      The `v-bind` object shape (rather than `:inert="!open"`) is
      load-bearing: `inert` is a BOOLEAN attribute that Vue does not know
      about, so `:inert="false"` renders `inert="false"` -- and a boolean
      attribute is active whenever it is PRESENT, whatever its value. That
      would leave the panel permanently inert, including while open. The
      attribute has to be absent, not false.
    -->
    <aside
      class="hf-drawer"
      :class="{ open }"
      role="dialog"
      aria-modal="true"
      :aria-label="panelLabel"
      :aria-hidden="open ? 'false' : 'true'"
      v-bind="open ? {} : { inert: '' }"
      data-testid="help-flyover-panel"
      @keydown.esc.stop="close"
    >
      <div class="hf-head">
        <div class="hf-head__text">
          <div class="hf-ttl">{{ panelTitle }}</div>
          <div class="hf-meta">{{ panelSubtitle }}</div>
        </div>

        <!--
          Open in a new tab. Does BOTH things Jake asked for: opens the
          article in a new browsing context AND dismisses the flyover.
          A <button> rather than an <a target="_blank"> because the
          dismissal is a real side effect -- an anchor that also mutates
          the parent surface on click is a worse thing to hand a keyboard
          or screen-reader user than a button that says what it does.
          The url is the UNDECORATED path: a new tab is not a frame and
          wants the full app chrome.
        -->
        <button
          type="button"
          class="hf-action"
          data-testid="help-flyover-new-tab"
          :aria-label="`Open ${panelTitle} in a new tab`"
          title="Open in a new tab"
          @click="openInNewTab"
        >
          <span class="tf-icon" aria-hidden="true">open_in_new</span>
          <span class="hf-action__label">Open in new tab</span>
        </button>

        <button
          type="button"
          class="hf-close"
          data-testid="help-flyover-close"
          aria-label="Close help"
          title="Close help"
          @click="close"
        >
          <span class="tf-icon" aria-hidden="true">close</span>
        </button>
      </div>

      <!--
        `v-if="open"` rather than v-show: while closed there is no help
        request, no same-origin frame in the tree, and reopening always
        re-resolves the article for whatever screen the reader is on now.
      -->
      <div class="hf-body" v-if="open">
        <iframe
          ref="frame"
          :src="framedHelpPath"
          :title="`Help: ${panelTitle}`"
          class="hf-frame"
          data-testid="help-flyover-iframe"
        ></iframe>
      </div>
    </aside>
  </div>
</template>

<script>
import { resolveHelpTarget } from "@/router/helpContextMap";
import { withHelpFramedFlag } from "@/router/helpFramed";

const AUDIENCE_TITLE = {
  owner: "Help",
  shopper: "Help",
  superadmin: "Platform help",
};

export default {
  name: "HelpFlyover",

  props: {
    /** Open/closed. Owned by the layout that mounts this. */
    open: {
      type: Boolean,
      default: false,
    },
    /**
     * Which audience tree to open: 'owner' | 'shopper'. Supplied by the
     * mounting LAYOUT, never by the URL -- same rule as HelpRenderer, and
     * it is what stops a storefront flyover resolving into the
     * distributor-gated owner tree.
     */
    audience: {
      type: String,
      required: true,
      validator: (value) => ["owner", "shopper", "superadmin"].includes(value),
    },
  },

  emits: ["close"],

  computed: {
    /**
     * The contextual help path for the CURRENT route -- undecorated.
     *
     * Computed rather than captured on open: this component is mounted
     * once by the layout and survives navigation, so a data property set
     * at mount would pin the flyover to whatever screen the reader first
     * loaded.
     */
    helpPath() {
      return resolveHelpTarget(this.$route, this.audience);
    },

    /** The same path, flagged so the framed copy renders without chrome. */
    framedHelpPath() {
      return withHelpFramedFlag(this.helpPath);
    },

    panelTitle() {
      return AUDIENCE_TITLE[this.audience] || "Help";
    },

    panelLabel() {
      return `${this.panelTitle} panel`;
    },

    panelSubtitle() {
      return this.audience === "shopper"
        ? "Guides for shopping this store"
        : "Guides for running your stores";
    },
  },

  watch: {
    /**
     * Move focus into the panel when it opens so a keyboard reader is not
     * left behind on the trigger, tabbing through the app underneath.
     * Focus is restored to the previously focused element on close --
     * both halves matter, and the second is the one usually forgotten.
     */
    open(isOpen) {
      if (isOpen) {
        this.lastFocused =
          typeof document !== "undefined" ? document.activeElement : null;
        this.$nextTick(() => {
          const closeBtn = this.$el?.querySelector?.(
            '[data-testid="help-flyover-close"]'
          );
          closeBtn?.focus?.();
        });
      } else if (this.lastFocused?.focus) {
        this.lastFocused.focus();
        this.lastFocused = null;
      }
    },
  },

  created() {
    this.lastFocused = null;
  },

  mounted() {
    // Document-level so Escape works regardless of where focus sits --
    // including inside the iframe's chrome. Same approach as the
    // storefront preview drawer's handleEscape.
    document.addEventListener("keydown", this.handleEscape);
  },

  beforeUnmount() {
    document.removeEventListener("keydown", this.handleEscape);
  },

  methods: {
    handleEscape(event) {
      if (event.key === "Escape" && this.open) this.close();
    },

    close() {
      this.$emit("close");
    },

    /**
     * BOTH actions, per Jake: open the article in a new tab AND dismiss
     * the flyover. Leaving the panel up behind a freshly opened tab would
     * mean returning to a covered app.
     *
     * `noopener` (and the matching `rel` semantics it implies) so the new
     * context cannot reach back through `window.opener`.
     */
    openInNewTab() {
      if (typeof window !== "undefined" && window.open) {
        window.open(this.helpPath, "_blank", "noopener,noreferrer");
      }
      this.close();
    },
  },
};
</script>

<style scoped>
/*
 * Mirrors the storefront preview drawer (StoreEditShell.vue
 * `.ces-preview-*`): fixed scrim, right-anchored panel translated off
 * screen while closed, transform transition in. Class names are the
 * flyover's own because the `ces-` block is scoped to the store editor
 * and this component renders on every distributor and storefront screen.
 *
 * z-index sits above the preview drawer's 90/91 so that help opened FROM
 * the store editor lands on top of the editor's own preview rather than
 * behind it.
 */
.hf-scrim {
  position: fixed;
  inset: 0;
  background: rgba(19, 55, 82, 0.42);
  backdrop-filter: blur(2px);
  z-index: 120;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.25s;
}

.hf-scrim.open {
  opacity: 1;
  pointer-events: auto;
}

.hf-drawer {
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: min(880px, 92vw);
  background: var(--tf-bg);
  z-index: 121;
  box-shadow: -12px 0 40px rgba(19, 55, 82, 0.18);
  transform: translateX(100%);
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  visibility: hidden;
}

.hf-drawer.open {
  transform: translateX(0);
  visibility: visible;
}

.hf-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--tf-border);
  background: var(--tf-surface-muted);
  flex-shrink: 0;
}

.hf-head__text {
  min-width: 0;
}

.hf-ttl {
  font: 500 14px var(--tf-font-sans, "Roboto", sans-serif);
  color: var(--tf-secondary);
}

.hf-meta {
  font: 400 12px var(--tf-font-sans, "Roboto", sans-serif);
  color: var(--tf-fg-4);
}

.hf-action {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--tf-border);
  background: var(--tf-bg);
  border-radius: 9999px;
  padding: 6px 14px;
  font: 500 12px var(--tf-font-sans, "Roboto", sans-serif);
  color: var(--tf-fg-4);
  cursor: pointer;
}

.hf-action:hover {
  color: var(--tf-secondary);
  border-color: var(--tf-secondary);
}

.hf-action .tf-icon {
  font-size: 14px;
}

.hf-close {
  border: none;
  background: transparent;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--tf-fg-4);
  flex-shrink: 0;
}

.hf-close:hover {
  background: var(--tf-surface-dark);
  color: var(--tf-secondary);
}

.hf-body {
  flex: 1;
  min-height: 0;
  display: flex;
}

.hf-frame {
  border: none;
  width: 100%;
  height: 100%;
  background: var(--tf-bg);
}

@media (max-width: 700px) {
  .hf-drawer {
    width: 100vw;
  }

  /* The label would crowd the head row on a phone; the icon plus its
     aria-label still carry the meaning. */
  .hf-action__label {
    display: none;
  }
}
</style>
